import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";

import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup, Textarea, Checkbox } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { Language } from "imports/Utils";

import { terminalPlanningNotesSchema, TerminalPlanningNotesSchemaInput, TerminalPlanningNotesSchemaOutput } from "view/terminalPlanning/utils/terminalPlanning.schema";
import { CargoAction } from "view/terminalPlanning/utils/terminalPlanning.types";

import { UpdateLastTerminalContactDateDocument, UpdateTerminalNotesDocument } from "__generated__/view/terminalPlanning/graphql/terminalPlanning.mutations.generated";

const UpdateTerminalPlannigNotesDialog = ({
	open,
	cargoAction,
	onClose,
	refetch,
}: {
	open: boolean;
	cargoAction: CargoAction | null;
	onClose: () => void;
	refetch: () => void;
}) => {

	const [ updateTerminalNotes, { loading: updateNotesLoading } ] = useMutation(UpdateTerminalNotesDocument);
	const [ updateLastTerminalContactDate, { loading: updateDateLoading } ] = useMutation(UpdateLastTerminalContactDateDocument);

	const loading = updateNotesLoading || updateDateLoading;

	const defaultValues = {
		notes: "",
		updateLastContactTime: true,
	};

	const { control, handleSubmit, reset } = useForm<TerminalPlanningNotesSchemaInput, unknown, TerminalPlanningNotesSchemaOutput>({
		resolver: zodResolver(terminalPlanningNotesSchema),
		defaultValues: defaultValues,
	});

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const save = (data: TerminalPlanningNotesSchemaOutput) => {
		if (cargoAction) {

			updateTerminalNotes({
				variables: {
					id: cargoAction?.id,
					notes: data.notes,
				},
			}).then(() => {
				if (data.updateLastContactTime && cargoAction.location) {
					updateLastTerminalContactDate({
						variables: {
							id: cargoAction.location.id,
						},
					}).then(() => {
						handleOnClose();
						refetch();
					});
				} else {
					handleOnClose();
					refetch();
				}
			});
		}
	};

	useEffect(() => {
		if (open && cargoAction) {
			reset({
				notes: cargoAction.terminalPlanningNotes ?? "",
				updateLastContactTime: true,
			});
		}
	}, [ open ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "TERMINAL_PLANNING_NOTES" })}
			form='terminalPlanningNotesForm'
			maxWidth="sm"
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="terminalPlanningNotesForm">
				<FormGroup label={Language("NOTES")} overflow="hidden">
					<Controller
						name="notes"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Textarea
									{...field}
									style={{ minHeight: "100px" }}
									onFocus={(e) => {
										const length = e.target.value.length;
										e.target.setSelectionRange(length, length);
									}}
									autoFocus />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<FormGroup
					label={intl.formatMessage({ id: "UPDATE_TERMINAL_LAST_CONTACT_TIME" })}
					labelPlacement="left-center"
					overflow="hidden">
					<Controller
						name="updateLastContactTime"
						control={control}
						render={({ field }) => (
							<Checkbox
								onChange={(event) => field.onChange(event.target.checked) }
								checked={field.value} />
						)} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default UpdateTerminalPlannigNotesDialog;
